'use client';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HeroIllustration } from '../components/HeroIllustration';
import { ArrowRight, Play, Sparkles, Shield, Users } from 'lucide-react';
import { useTranslations } from 'next-intl';

export function Hero() {
  const t = useTranslations('landing.hero');

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 gradient-mesh" />

      {/* Decorative elements */}
      <div className="absolute top-20 start-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 end-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="container-landing relative z-10 pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left/Start content */}
          <div className="text-center lg:text-start order-2 lg:order-1">
            {/* Badge */}
            <div className="inline-flex animate-fade-in-up">
              <Badge
                variant="secondary"
                className="px-4 py-1.5 text-sm font-medium bg-accent/80 border border-primary/20 text-foreground gap-2"
              >
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                {t('badge')}
              </Badge>
            </div>

            {/* Headline */}
            <h1 className="mt-6 md:mt-8 animate-fade-in-up stagger-1">
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-brand font-bold tracking-tight text-balance leading-[1.1]">
                {t('headline1')}
              </span>
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-brand font-bold tracking-tight text-balance leading-[1.1] mt-2">
                {t('headline2')}{' '}
                <span className="text-primary">{t('headline3')}</span>
              </span>
            </h1>

            {/* Subheadline */}
            <p className="mt-6 md:mt-8 text-lg md:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 text-balance animate-fade-in-up stagger-2">
              {t('subheadline')}
            </p>

            {/* CTA Buttons */}
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up stagger-3">
              <Button
                size="lg"
                className="text-base px-8 h-12 md:h-14 shadow-xl shadow-primary/25 hover:shadow-primary/40 transition-all group"
                asChild
              >
                <Link href="/signup">
                  {t('cta')}
                  <ArrowRight className="ms-2 w-4 h-4 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform rtl:rotate-180" />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="text-base px-8 h-12 md:h-14 group"
              >
                <Play className="me-2 w-4 h-4 group-hover:scale-110 transition-transform" />
                {t('demo')}
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="mt-10 md:mt-12 flex flex-wrap items-center gap-6 justify-center lg:justify-start animate-fade-in-up stagger-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="flex -space-x-2 rtl:space-x-reverse">
                  {[
                    'bg-amber-200',
                    'bg-rose-200',
                    'bg-emerald-200',
                    'bg-sky-200',
                  ].map((color, i) => (
                    <div
                      key={i}
                      className={`w-8 h-8 rounded-full ${color} border-2 border-background flex items-center justify-center`}
                    >
                      <Users className="w-3.5 h-3.5 text-foreground/60" />
                    </div>
                  ))}
                </div>
                <span className="font-medium">500+</span> {t('producers')}
              </div>
              <div className="hidden sm:block w-px h-6 bg-border" />
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-emerald-600" />
                <span>{t('security')}</span>
              </div>
            </div>
          </div>

          {/* Right/End content - Dashboard Preview */}
          <div className="flex justify-center lg:justify-end order-1 lg:order-2">
            <HeroIllustration />
          </div>
        </div>
      </div>

      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 start-0 end-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
          preserveAspectRatio="none"
        >
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="hsl(var(--foreground))"
          />
        </svg>
      </div>
    </section>
  );
}
